// Polished "coming soon" page wrapped in PortalShell. Lets every
// sidebar menu item route somewhere reasonable, even before the
// underlying feature is built.
//
// Use ONE component instead of 15 stubs so we don't drift on copy
// or layout.

import { ArrowRight, Clock, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PortalShell, type NavSection } from '../components/shared/PortalShell';
import { Card } from '../components/shared/primitives';

interface Props {
  nav: NavSection[];
  activeId: string;
  title: string;
  subtitle?: string;
  phase: string;             // e.g. "Phase 2 — Stripe + quotes"
  body: string;
  bullets?: string[];
  backLink?: { to: string; label: string };
}

export function ShellPlaceholder({ nav, activeId, title, subtitle, phase, body, bullets, backLink }: Props) {
  return (
    <PortalShell nav={nav} activeId={activeId} title={title} subtitle={subtitle}>
      <div className="max-w-2xl">
        <Card className="bg-gradient-to-br from-brand-50/40 to-[var(--color-warning-bg)]/20">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[var(--color-border)] text-[10.5px] uppercase tracking-[0.15em] font-semibold text-brand-700">
            <Clock className="w-3 h-3" /> {phase}
          </span>
          <h2 className="mt-3 text-[20px] font-semibold tracking-tight text-[var(--color-ink)]">{title}</h2>
          <p className="mt-2 text-[14px] text-[var(--color-secondary)] leading-relaxed">{body}</p>

          {bullets && bullets.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-[13px] text-[var(--color-body)]">
                  <span className="inline-block w-1 h-1 rounded-full bg-[var(--color-muted)] mt-2" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 flex gap-3 flex-wrap text-[12.5px]">
            {backLink && (
              <Link to={backLink.to} className="inline-flex items-center gap-1 text-brand-600 font-semibold hover:underline">
                {backLink.label} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
            <a href="https://github.com/michelevens/lakaycart-app/blob/main/docs/USER_FLOWS.md" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--color-secondary-2)] hover:text-[var(--color-ink)]">
              <FileText className="w-3 h-3" /> Full roadmap
            </a>
          </div>
        </Card>
      </div>
    </PortalShell>
  );
}
